import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { NavigateFunction } from 'react-router-dom';

import type { UnsavedGuardBypassState } from '../../../../core-components/theme/components/Screen';
import { feedHasAlert } from '../../domain/feed';
import { resolveLink, withStartIntent } from '../../domain/links';
import { closeShownAlerts } from '../../push/browser';
import { workerMessageOf } from '../../push/messages';
import type { WorkerMessage } from '../../push/messages';
import type { PushPayload } from '../../types/domain.types';
import { myFeedQueryOptions } from '../../utils/queryOptions';

import { useSyncThisDevice } from './useTurnOnThisDevice';

const PAST_THE_GUARD: UnsavedGuardBypassState = { bypassUnsavedGuard: true };
const HANDLED = { handled: true } as const;

type ClickMessage = Extract<WorkerMessage, { type: 'ctl-notification-click' }>;

export interface WorkerMessageHandlers {
  readonly queryClient: QueryClient;
  readonly navigate: NavigateFunction;
  readonly resync: () => void;
  readonly closeShown: (tag: string) => Promise<void>;
}

const openFromNotification = (navigate: NavigateFunction, message: ClickMessage): void => {
  const target = resolveLink(message.link, window.location.origin);
  if (target.kind === 'external') {
    window.open(target.href, '_blank', 'noopener,noreferrer');
    return;
  }
  const path = message.action === 'start' ? withStartIntent(target.path) : target.path;
  navigate(path, message.severity === 'EMERGENCY' ? { state: PAST_THE_GUARD } : undefined);
};

const showInApp = async ({ queryClient, closeShown }: WorkerMessageHandlers, payload: PushPayload | null): Promise<boolean> => {
  const { queryKey } = myFeedQueryOptions();
  await queryClient.invalidateQueries({ queryKey });
  if (payload === null || payload.issueId === null) return false;
  const page = queryClient.getQueryData(queryKey);
  if (page === undefined || !feedHasAlert(page.items, payload.issueId, payload.step)) return false;
  await closeShown(payload.issueId);
  return true;
};

export const handleWorkerMessage = async (handlers: WorkerMessageHandlers, message: WorkerMessage): Promise<boolean> => {
  switch (message.type) {
    case 'ctl-alert':
      return showInApp(handlers, message.payload);
    case 'ctl-notification-click':
      openFromNotification(handlers.navigate, message);
      return true;
    case 'ctl-subscription-changed':
      handlers.resync();
      return true;
  }
};

const answerTo =
  (port: MessagePort | undefined) =>
  (handled: boolean): void => {
    if (handled) port?.postMessage(HANDLED);
  };

export const useServiceWorkerMessages = (): void => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutate: resync } = useSyncThisDevice();

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const container = navigator.serviceWorker;
    const handlers: WorkerMessageHandlers = { queryClient, navigate, resync: () => resync(), closeShown: closeShownAlerts };
    const onMessage = (event: MessageEvent): void => {
      const message = workerMessageOf(event.data);
      if (message === null) return;
      void handleWorkerMessage(handlers, message)
        .catch(() => false)
        .then(answerTo(event.ports[0]));
    };
    container.addEventListener('message', onMessage);
    container.startMessages();
    return () => container.removeEventListener('message', onMessage);
  }, [queryClient, navigate, resync]);
};
